import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter, Button, Input, Textarea, ImageUpload } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import type { Widget, WidgetData } from '@/types';

const socialPlatforms = [
  'Instagram',
  'TikTok',
  'YouTube',
  'Twitter',
  'Facebook',
  'LinkedIn',
  'Spotify',
  'WhatsApp',
];

const widgetTypeLabels: Record<string, string> = {
  link: 'widgets.link',
  text: 'widgets.text',
  photo: 'widgets.photo',
  product: 'widgets.product',
  social: 'widgets.social',
  music: 'widgets.music',
  voice: 'widgets.voice',
  onelink: 'widgets.oneLink',
  content: 'widgets.content',
};

interface EditWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  widget: Widget;
  onSave: (widgetId: string, data: WidgetData) => Promise<void>;
}

const EditWidgetModal = ({ isOpen, onClose, widget, onSave }: EditWidgetModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Initialize form data from widget.data when widget changes
  useEffect(() => {
    if (widget) {
      const data = widget.data as unknown as Record<string, unknown>;
      const initial: Record<string, string> = {};
      for (const [key, value] of Object.entries(data)) {
        initial[key] = value != null ? String(value) : '';
      }
      setFormData(initial);
    }
  }, [widget]);

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let widgetData: WidgetData;

      switch (widget.type) {
        case 'link':
          widgetData = {
            title: formData.title || t('widgets.untitledLink'),
            url: formData.url || '',
            ...(formData.icon && { icon: formData.icon }),
          };
          break;
        case 'text':
          widgetData = {
            content: formData.content || '',
          };
          break;
        case 'photo':
          widgetData = {
            imageUrl: formData.imageUrl || '',
            ...(formData.caption && { caption: formData.caption }),
          };
          break;
        case 'product':
          widgetData = {
            title: formData.title || t('widgets.product'),
            price: parseFloat(formData.price) || 0,
            currency: formData.currency || 'USD',
            imageUrl: formData.imageUrl || '',
            url: formData.url || '',
            ...(formData.description && { description: formData.description }),
          };
          break;
        case 'social':
          widgetData = {
            platform: formData.platform || 'Instagram',
            username: formData.username || '',
            url: formData.url || '',
          };
          break;
        case 'music':
          widgetData = {
            platform: (formData.platform as 'spotify' | 'apple-music') || 'spotify',
            embedUrl: formData.embedUrl || '',
            ...(formData.title && { title: formData.title }),
          };
          break;
        case 'voice':
          widgetData = {
            audioUrl: formData.audioUrl || '',
            ...(formData.title && { title: formData.title }),
          };
          break;
        case 'onelink':
          widgetData = {
            appName: formData.appName || t('widgets.placeholderAppName'),
            ...(formData.appStoreUrl && { appStoreUrl: formData.appStoreUrl }),
            ...(formData.playStoreUrl && { playStoreUrl: formData.playStoreUrl }),
            ...(formData.iconUrl && { iconUrl: formData.iconUrl }),
          };
          break;
        case 'content':
          widgetData = {
            ...(formData.title && { title: formData.title }),
            maxItems: parseInt(formData.maxItems) || 6,
          };
          break;
        default:
          return;
      }

      await onSave(widget.id, widgetData);
    } catch (error) {
      console.error('Failed to update widget:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderConfigForm = () => {
    switch (widget.type) {
      case 'link':
        return (
          <>
            <Input
              label={t('widgets.titleLabel')}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('widgets.placeholderLinkTitle')}
            />
            <Input
              label={t('widgets.urlLabel')}
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com"
            />
          </>
        );

      case 'text':
        return (
          <Textarea
            label={t('widgets.contentLabel')}
            value={formData.content || ''}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder={t('widgets.placeholderWriteSomething')}
            rows={4}
          />
        );

      case 'photo':
        return (
          <>
            <ImageUpload
              value={formData.imageUrl || ''}
              onChange={(url) => setFormData({ ...formData, imageUrl: url })}
              storagePath={`widgets/${user?.uid}`}
            />
            <Input
              label={t('widgets.captionLabel')}
              value={formData.caption || ''}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              placeholder={t('widgets.placeholderPhotoCaption')}
            />
          </>
        );

      case 'product':
        return (
          <>
            <Input
              label={t('widgets.productName')}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('widgets.placeholderProductName')}
            />
            <Textarea
              label={t('widgets.descriptionLabel')}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('widgets.placeholderProductDescription')}
              rows={2}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('widgets.priceLabel')}
                type="number"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="29.99"
              />
              <Input
                label={t('widgets.currencyLabel')}
                value={formData.currency || 'USD'}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                placeholder="USD"
              />
            </div>
            <Input
              label={t('widgets.imageUrlLabel')}
              value={formData.imageUrl || ''}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              placeholder="https://example.com/product.jpg"
            />
            <Input
              label={t('widgets.productUrl')}
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://shop.example.com/product"
            />
          </>
        );

      case 'social':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('widgets.platformLabel')}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {socialPlatforms.map((platform) => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => setFormData({ ...formData, platform })}
                    className={`p-2 text-xs rounded-xl border transition-colors ${
                      formData.platform === platform
                        ? 'border-lime-400 bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400'
                        : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500 dark:text-gray-300'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
            <Input
              label={t('widgets.usernameLabel')}
              value={formData.username || ''}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="@username"
            />
            <Input
              label={t('widgets.profileUrl')}
              value={formData.url || ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://instagram.com/username"
            />
          </>
        );

      case 'music':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('widgets.platformLabel')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['spotify', 'apple-music'].map((platform) => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => setFormData({ ...formData, platform })}
                    className={`p-3 text-sm rounded-xl border transition-colors ${
                      formData.platform === platform
                        ? 'border-lime-400 bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400'
                        : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500 dark:text-gray-300'
                    }`}
                  >
                    {platform === 'spotify' ? 'Spotify' : 'Apple Music'}
                  </button>
                ))}
              </div>
            </div>
            <Input
              label={t('widgets.songPlaylistUrl')}
              value={formData.embedUrl || ''}
              onChange={(e) => setFormData({ ...formData, embedUrl: e.target.value })}
              placeholder="https://open.spotify.com/track/..."
            />
            <Input
              label={t('widgets.titleOptional')}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('widgets.placeholderFavoriteSong')}
            />
          </>
        );

      case 'voice':
        return (
          <>
            <Input
              label={t('widgets.audioUrl')}
              value={formData.audioUrl || ''}
              onChange={(e) => setFormData({ ...formData, audioUrl: e.target.value })}
              placeholder="https://example.com/audio.mp3"
            />
            <Input
              label={t('widgets.titleOptional')}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('widgets.placeholderVoiceMessage')}
            />
          </>
        );

      case 'onelink':
        return (
          <>
            <Input
              label={t('widgets.appName')}
              value={formData.appName || ''}
              onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
              placeholder={t('widgets.placeholderAppName')}
            />
            <Input
              label={t('widgets.appIconUrl')}
              value={formData.iconUrl || ''}
              onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
              placeholder="https://example.com/icon.png"
            />
            <Input
              label={t('widgets.appStoreUrl')}
              value={formData.appStoreUrl || ''}
              onChange={(e) => setFormData({ ...formData, appStoreUrl: e.target.value })}
              placeholder="https://apps.apple.com/app/..."
            />
            <Input
              label={t('widgets.playStoreUrl')}
              value={formData.playStoreUrl || ''}
              onChange={(e) => setFormData({ ...formData, playStoreUrl: e.target.value })}
              placeholder="https://play.google.com/store/apps/..."
            />
          </>
        );

      case 'content':
        return (
          <>
            <Input
              label={t('widgets.titleOptional')}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('widgets.placeholderContentTitle')}
            />
            <Input
              label={t('widgets.maxItemsLabel')}
              type="number"
              value={formData.maxItems || '6'}
              onChange={(e) => setFormData({ ...formData, maxItems: e.target.value })}
              placeholder="6"
            />
          </>
        );

      default:
        return null;
    }
  };

  const typeLabel = t(widgetTypeLabels[widget.type] || 'widgets.widget');

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('widgets.editType', { type: typeLabel })}
      size="md"
    >
      <div className="space-y-4">
        {renderConfigForm()}

        <ModalFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isLoading}>
            {t('profile.saveChanges')}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default EditWidgetModal;
